"use client";
// «Completa tu experiencia»: sugerencias según lo que ya está en el carrito, y la ruta del explorador (más tours, mejor precio).
import { useEffect, useMemo, useState } from "react";
import { tours } from "@/content/catalogo";
import { usePreciosVigentes } from "@/components/usePreciosVigentes";
import { useContenidoTours } from "@/components/useContenidoTours";
import { formatoMXN } from "@/lib/dinero";
import { recomendar, rutaDelExplorador, type PromoPublica } from "@/lib/recomendaciones";
import { Img } from "@/components/Img";

type Publica = PromoPublica & { parametros?: PromoPublica["parametros"] };
function usePromosPublicas() {
  const [p, setP] = useState<Publica[]>([]);
  useEffect(() => { fetch("/api/promociones/publicas").then((r) => (r.ok ? r.json() : [])).then((d) => setP(Array.isArray(d) ? d : [])).catch(() => setP([])); }, []);
  return p;
}
const catalogo = tours.map((t) => ({ slug: t.slug, nombre: t.nombre.es, nombreEn: t.nombre.en, categoria: t.categoria, adultoCentavos: t.adultoCentavos, imagen: t.imagenes[0]?.archivo ?? "", duracionMin: t.duracionMin }));

export function RutaExplorador({ idioma, tours: n }: { idioma: "es" | "en"; tours: number }) {
  const es = idioma === "es", promos = usePromosPublicas();
  const tramos = promos.find((p) => p.tipo === "volumen_tours")?.parametros?.tramos ?? [];
  const r = rutaDelExplorador(n, tramos);
  if (!r.pasos.length) return null;
  const medallas = ["🥉", "🥈", "🥇", "🏆"];
  return (
    <div className="ruta-explorador" role="group" aria-label={es ? "Ruta del explorador" : "Explorer route"}>
      <p className="ruta-t"><b>{es ? "Ruta del explorador" : "Explorer route"}</b> {r.completo ? (es ? "· ¡máximo ahorro desbloqueado!" : "· max savings unlocked!") : (es ? `· suma ${r.faltan} tour${r.faltan === 1 ? "" : "s"} y ahorras ${r.siguiente!.porciento}%` : `· add ${r.faltan} more tour${r.faltan === 1 ? "" : "s"} and save ${r.siguiente!.porciento}%`)}</p>
      <ol className="ruta-pasos">
        {r.pasos.map((p, i) => <li key={p.tours} data-logrado={n >= p.tours}><span aria-hidden="true">{medallas[i] ?? "⭐"}</span><b>{p.tours} tours</b><small>{p.porciento}%</small></li>)}
      </ol>
    </div>
  );
}

export function Recomendaciones({ idioma, slugs, fecha, personas }: { idioma: "es" | "en"; slugs: string[]; fecha?: string; personas?: number }) {
  const es = idioma === "es", promos = usePromosPublicas(), precios = usePreciosVigentes(), contenido = useContenidoTours();
  const vigente = useMemo(() => catalogo.map((t) => {
    const c = contenido?.[t.slug];
    return { ...t, ...(precios?.[t.slug] ? { adultoCentavos: precios[t.slug].adultoCentavos } : {}), ...(c ? { nombre: c.nombre.es || t.nombre, nombreEn: c.nombre.en || t.nombreEn, imagen: c.imagenes[0]?.archivo ?? t.imagen } : {}) };
  }), [precios, contenido]);
  const lista = useMemo(() => recomendar({ enCarrito: slugs, idioma, fecha, personas }, vigente, promos, 3), [slugs, idioma, fecha, personas, promos, vigente]);
  if (!lista.length) return null;
  return (
    <section className="recomienda" aria-labelledby="rec-t">
      <h2 id="rec-t">{es ? "Completa tu experiencia" : "Complete your experience"}</h2>
      <p className="tenue">{es ? "Lo que mejor va con lo que elegiste." : "What goes best with what you picked."}</p>
      <ul>
        {lista.map((r) => (
          <li key={r.clave}>
            <a href={r.href}>
              {r.imagen && <Img src={r.imagen} alt="" loading="lazy" />}
              <span className="rec-cuerpo">
                <b>{es || r.tipo === "experiencia" ? r.titulo : (catalogo.find((t) => t.slug === r.clave)?.nombreEn ?? r.titulo)}</b>
                <small>{r.motivo}</small>
                {r.ahorro && <em>🎁 {r.ahorro}</em>}
                {r.precioCentavos !== undefined && <span className="rec-precio">{es ? "desde" : "from"} {formatoMXN(r.precioCentavos, idioma)}</span>}
              </span>
              <span className="rec-ir" aria-hidden="true">{r.tipo === "tour" ? (es ? "Ver →" : "View →") : "→"}</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
